/**
 * Microsoft Graph Service
 *
 * Fetches user profile data (name, title, email, phone, office, etc.)
 * directly from Microsoft 365 / Azure AD via Microsoft Graph API.
 *
 * Uses MSAL (Microsoft Authentication Library) for token acquisition.
 */

declare const Office: any;

export interface GraphUserProfile {
    id: string;
    displayName: string;
    jobTitle: string;
    mobilePhone?: string;
    businessPhones?: string[];
    officeLocation?: string;
    mail: string;
    userPrincipalName: string;
    department?: string;
    companyName?: string;
}

const GRAPH_ENDPOINT = "https://graph.microsoft.com/v1.0/me";

/**
 * Cache key for storing fetched user profile in localStorage
 */
const CACHE_KEY = "signaflow_user_profile";
//const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Retrieves current user's profile from Microsoft Graph API with caching.
 *
 * Flow:
 * 1. Check localStorage cache (if fresh, return cached)
 * 2. If cache miss or expired, fetch from Graph API
 * 3. Store in cache
 * 4. Return profile
 *
 * @returns GraphUserProfile with name, title, phone, office location, etc.
 * @throws Error if Graph API call fails
 */
export async function getUserProfileFromGraph(): Promise<GraphUserProfile> {
    // --- CHECK CACHE ---
    const cached = getProfileFromCache();
    if (cached && cached != null) {
        console.log("[GraphService] Returning cached user profile");
        return cached;
    }

    // --- FETCH FROM GRAPH API ---
    console.log("[GraphService] Fetching user profile from Microsoft Graph...");

    try {
        const token = await getAccessToken();
        const response = await fetch(GRAPH_ENDPOINT, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json"
            }
        });

        if (!response.ok) {
            throw new Error(`Graph API error: ${response.status} ${response.statusText}`);
        }

        const profile: GraphUserProfile = await response.json();

        // --- STORE IN CACHE ---
        cacheProfile(profile);

        console.log("[GraphService] User profile fetched and cached:", {
            displayName: profile.displayName,
            jobTitle: profile.jobTitle,
            mail: profile.mail
        });

        return profile;
    } catch (error) {
        console.error("[GraphService] Failed to fetch user profile:", error);
        throw error;
    }
}

/**
 * Clears cached user profile (useful for logout or manual refresh)
 */
export function clearProfileCache(): void {
    localStorage.removeItem(CACHE_KEY);
    console.log("[GraphService] Profile cache cleared");
}

/**
 * Retrieves cached profile if fresh, null if expired or missing
 */
function getProfileFromCache(): GraphUserProfile | null {
    try {
        const cached = localStorage.getItem(CACHE_KEY);
        if (!cached || cached == null)
            return null;

        const { profile, timestamp } = JSON.parse(cached);
        const age = Date.now() - timestamp;

        if (age > CACHE_TTL_MS) {
            console.log("[GraphService] Cache expired, will fetch fresh");
            localStorage.removeItem(CACHE_KEY);
            return null;
        }

        console.log(`[GraphService] Cache hit (age: ${Math.round(age / 1000)}s)`);
        return profile;
    } catch (e) {
        console.warn("[GraphService] Cache read failed, clearing:", e);
        localStorage.removeItem(CACHE_KEY);
        return null;
    }
}

/**
 * Stores profile in localStorage with timestamp
 */
function cacheProfile(profile: GraphUserProfile): void {
    try {
        localStorage.setItem(
            CACHE_KEY,
            JSON.stringify({
                profile,
                timestamp: Date.now()
            })
        );
    } catch (e) {
        console.warn("[GraphService] Cache write failed:", e);
    }
}

declare const OfficeRuntime: any;

/**
 * Acquires access token for Microsoft Graph API.
 *
 * Checks modern Office.auth.getAccessToken() first, followed by
 * OfficeRuntime.auth.getAccessToken(), and falls back to legacy
 * Office.context.auth.getAccessTokenAsync.
 *
 * @returns Bearer token for Graph API
 * @throws Error if authentication fails
 */
async function getAccessToken(): Promise<string> {
  // 1. Try modern Office.auth.getAccessToken silently
  if (typeof Office !== "undefined" && typeof Office.auth?.getAccessToken === "function") {
    console.log("[GraphService] Requesting token silently via modern Office.auth.getAccessToken...");
    return await Office.auth.getAccessToken({
      allowSignInPrompt: false,
      allowConsentPrompt: false
    });
  }

  // 2. Try OfficeRuntime.auth.getAccessToken silently
  if (typeof OfficeRuntime !== "undefined" && typeof OfficeRuntime?.auth?.getAccessToken === "function") {
    console.log("[GraphService] Requesting token silently via OfficeRuntime.auth.getAccessToken...");
    return await OfficeRuntime.auth.getAccessToken({
      allowSignInPrompt: false,
      allowConsentPrompt: false
    });
  }

  // 3. Fallback to legacy Office.context.auth.getAccessTokenAsync
  const authContext = typeof Office !== "undefined" ? Office.context?.auth : undefined;
  if (authContext && typeof authContext.getAccessTokenAsync === "function") {
    console.log("[GraphService] Requesting token via legacy Office.context.auth.getAccessTokenAsync...");
    return new Promise((resolve, reject) => {
      authContext.getAccessTokenAsync(
        { allowSignInPrompt: false, allowConsentPrompt: false },
        (result: any) => {
          if (result.status === "succeeded") {
            resolve(result.value);
          } else {
            reject(
              new Error(
                `Token acquisition failed: ${result.error?.message || "Unknown error"}`
              )
            );
          }
        }
      );
    });
  }

  throw new Error(
    "Office SSO getAccessToken is not available in this client environment."
  );
}
